import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { s3Upload } from '@core/react-query/helpers'
import { ICompanyCompliancesResponse } from '@core/entities/company'
import { CompanyComplianceRequestTypes } from './requestTypes'

const companyComplianceQueryKeys = createQueryKeys('companyCompliance', {
  list: ({
    organisationId,
    companyId,
  }: CompanyComplianceRequestTypes['getCompanyCompliances']) => {
    return {
      queryKey: [{ organisationId, companyId }],
      queryFn: async (): Promise<ICompanyCompliancesResponse> => {
        const { data } = await APIAxiosInstance.get(
          `organisations/${organisationId}/companies/${companyId}/compliances`,
        )
        return data
      },
    }
  },
})

const companyComplianceMutationKeys = createMutationKeys('companyCompliance', {
  uploadDocument: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceId,
      file,
    }: CompanyComplianceRequestTypes['uploadComplianceDocument']) => {
      const key = await s3Upload({ file, folderName: 'company_compliance' })
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/compliances/${complianceId}/upload`,
        { documents: { data: [{ key }] } },
      )
      return data
    },
  },
  approveCompliance: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceId,
      expiryDate,
    }: CompanyComplianceRequestTypes['approveCompliance']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/compliances/${complianceId}/approve`,
        { expiryDate },
      )
      return data
    },
  },
  rejectCompliance: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceId,
      reason,
    }: CompanyComplianceRequestTypes['rejectCompliance']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/compliances/${complianceId}/reject`,
        { reason },
      )
      return data
    },
  },
  forceExpireCompliance: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceId,
    }: CompanyComplianceRequestTypes['forceExpireCompliance']) => {
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/companies/${companyId}/compliances/${complianceId}/force-expire`,
      )
      return data
    },
  },
  unassignCompliance: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      companyId,
      complianceId,
    }: CompanyComplianceRequestTypes['unassignCompliance']) => {
      const { data } = await APIAxiosInstance.delete(
        `organisations/${organisationId}/companies/${companyId}/compliances/${complianceId}`,
      )
      return data
    },
  },
})

export const companyComplianceKeys = mergeQueryKeys(
  companyComplianceQueryKeys,
  companyComplianceMutationKeys,
).companyCompliance
